import { Link } from 'react-router-dom';
import React, { useMemo } from 'react';

export interface BoxProps {
  title: string;
  href: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  count: string;
  ownerId: string;
}

export interface BoxesProps {
  trips?: BoxProps[];
  createBoxClick?: () => void | Promise<void>;
  useReactNavigation?: boolean;
}

export const TripBoxes = React.memo((props: BoxesProps) => {
  const trips = props.trips ? props.trips : [];

  const groupTripsByYear = (trips: BoxProps[]) => {
    const grouped = trips.reduce((acc, trip) => {
      const year = new Date(trip.startDate).getFullYear().toString();
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(trip);
      return acc;
    }, {} as Record<string, BoxProps[]>);

    Object.keys(grouped).forEach((year) => {
      grouped[year].sort(
        (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      );
    });

    return Object.keys(grouped)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .reduce((acc, year) => {
        acc[year] = grouped[year];
        return acc;
      }, {} as Record<string, BoxProps[]>);
  };

  const tripsByYear = useMemo(() => groupTripsByYear(trips), [trips]);

  return (
    <div className="m-auto max-w-[70%]">
      {props.createBoxClick && (
        <button
          type="button"
          onClick={props.createBoxClick}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm
                  hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                  focus-visible:outline-indigo-600"
        >
          + Add Trip
        </button>
      )}

      {Object.entries(tripsByYear)
        .reverse()
        .map(([year, yearTrips]) => (
          <div key={year} className="mb-16">
            <div className="flex items-center mb-8">
              <div className="flex-1 border-t border-gray-300"></div>
              <h2 className="px-4 text-xl font-semibold text-gray-700 bg-transparent">{year}</h2>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20">
              {yearTrips.map((trip, index) => (
                <Link
                  key={`${year}-${index}`}
                  to={trip.href}
                  className="flex flex-col items-start group relative cursor-pointer"
                  {...(!props.useReactNavigation && {
                    onClick: (e) => {
                      e.preventDefault();
                      window.location.href = trip.href;
                    },
                  })}
                >
                  <div className="relative w-full">
                    <img
                      alt=""
                      src={trip.imageUrl}
                      className="aspect-video w-full rounded-2xl bg-gray-100 object-cover sm:aspect-[2/1]
                              lg:aspect-[3/2]"
                      onError={(e) => (e.currentTarget.src = '/default-trip-header.jpg')}
                    />
                    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
                    {trip.count && (
                      <div
                        className="absolute bottom-2 right-2 z-10 rounded-full bg-gray-900/80 px-3 py-1.5 text-xs
                       font-medium text-white backdrop-blur-sm"
                      >
                        {trip.count}
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <div className="group relative">
                      <div className="relative mt-3 flex items-center gap-x-4">
                        <img
                          src={`https://media.${process.env.REACT_APP_HOST}/users/${
                            trip.ownerId
                          }-thumbnail.jpg?v=${Date.now()}`}
                          alt=""
                          className="size-10 rounded-full bg-gray-100"
                          onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
                        />
                        <div className="flex flex-col">
                          <h3 className="text-lg/6 font-semibold text-gray-900 group-hover:text-gray-600">
                            {trip.title}
                          </h3>
                          {trip.startDate && trip.endDate && (
                            <div className="flex items-center gap-x-1 text-xs">
                              <time dateTime={trip.startDate} className="text-gray-500">
                                {trip.startDate}
                              </time>
                              {' - '}
                              <time dateTime={trip.endDate} className="text-gray-500">
                                {trip.endDate}
                              </time>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
});
